import React, { useState } from 'react';
import { registrationsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { User, Mail, Phone, Loader } from 'lucide-react';

const RegistrationForm = ({ event, registrationType = 'online', selectedTier, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    customFieldValues: {}
  });
  const [loading, setLoading] = useState(false);

  const getTicketPrice = () => {
    if (!selectedTier || !event.ticketTiers || !Array.isArray(event.ticketTiers)) return 0;
    const tier = event.ticketTiers.find(t => t.name.toLowerCase() === selectedTier.toLowerCase());
    return tier ? tier.price : 0;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      customFieldValues: {
        ...formData.customFieldValues,
        [fieldName]: value
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    // Validate custom fields
    if (event.customFields && Array.isArray(event.customFields)) {
      for (const field of event.customFields) {
        if (field.required && !formData.customFieldValues[field.fieldName]) {
          toast.error(`Please fill in ${field.fieldLabel}`);
          return;
        }
      }
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    setLoading(true);

    try {
      // Extract eventId properly - handle both direct and nested structures
      const eventIdValue = event?.eventId || event?.data?.eventId || event?._id;
      
      if (!eventIdValue) {
        toast.error('Event ID is missing. Please try again.');
        setLoading(false);
        return;
      }

      // Normalize ticketTier to lowercase and ensure it's a valid value
      let tierValue = selectedTier || (Array.isArray(event.ticketTiers) && event.ticketTiers.length > 0 ? event.ticketTiers[0].name : 'silver');
      tierValue = tierValue.toLowerCase();
      
      // Ensure it's one of the valid enum values
      const validTiers = ['silver', 'gold', 'platinum'];
      if (!validTiers.includes(tierValue)) {
        tierValue = 'silver'; // Default to silver if invalid
      }

      const registrationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.replace(/\D/g, ''),
        eventId: eventIdValue,
        registrationType,
        ticketTier: tierValue,
        ticketPrice: getTicketPrice(),
        customFieldValues: formData.customFieldValues
      };
      
      console.log('📤 Submitting registration:', registrationData);

      const response = await registrationsAPI.create(registrationData);
      
      console.log('✅ Registration response:', response);
      
      toast.success(
        registrationType === 'online' 
          ? 'Registration successful! QR code will be sent to your email.' 
          : 'Registration successful! Please collect your QR slip.'
      );
      
      if (onSuccess) {
        // Pass the correct data structure - handle both response.data and response.data.data
        const registrationResult = response.data?.data || response.data;
        onSuccess(registrationResult);
      } else {
        setFormData({ name: '', email: '', phone: '', customFieldValues: {} });
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      console.error('❌ Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
        Register for {event.name}
      </h2>
      
      {/* Ticket Tier Display */}
      {selectedTier && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            Selected Tier: {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)}
          </h3>
          <p className="text-2xl font-bold text-blue-600">
            ₹{getTicketPrice()}
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <div className="relative">
            <User className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
              placeholder="Enter your full name"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
              placeholder="Enter your email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="relative">
            <Phone className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
              placeholder="Enter your 10-digit phone number"
              maxLength="15"
            />
          </div>
        </div>

        {/* Dynamic Custom Fields */}
        {event.customFields && Array.isArray(event.customFields) && event.customFields.map((field) => (
          <div key={field.fieldName}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.fieldLabel} {field.required && <span className="text-red-500">*</span>}
            </label>
            {field.fieldType === 'text' && (
              <input
                type="text"
                value={formData.customFieldValues[field.fieldName] || ''}
                onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                required={field.required}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
                placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
              />
            )}
            {field.fieldType === 'textarea' && (
              <textarea
                value={formData.customFieldValues[field.fieldName] || ''}
                onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                required={field.required}
                rows="3"
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
                placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
              />
            )}
            {field.fieldType === 'dropdown' && (
              <select
                value={formData.customFieldValues[field.fieldName] || ''}
                onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                required={field.required}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
              >
                <option value="">Select {field.fieldLabel}</option>
                {field.options?.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}
          </div>
        ))}

        <button
          type="submit"
          disabled={loading}
          className="w-full btn-primary flex items-center justify-center space-x-2 py-3 sm:py-4 min-h-[44px] text-sm sm:text-base"
        >
          {loading && <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />}
          <span>{loading ? 'Registering...' : 'Register Now'}</span>
        </button>
      </form>
      
      {registrationType === 'online' && (
        <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-600 text-center">
          Your QR code will be sent to your email address after registration.
        </p>
      )}
    </div>
  );
};

export default RegistrationForm;
